import { memo, useCallback, useMemo } from 'react';

import { Checkbox } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowsProp } from '@mui/x-data-grid-pro';

import { CissNumberField, CissTextField } from 'components/fields';
import { PanelGrid } from 'components/grid';
import { useQueryConfiguracaoPropriedade } from 'pages/cadastro/loja/hooks';
import { LojaConfiguracao } from 'pages/cadastro/loja/types';
import { getTypeChave } from 'pages/cadastro/loja/utils';

/**
 * Retorna as linhas da grid
 */
const getRows = (data: LojaConfiguracao[] = []): GridRowsProp =>
    data.map((item, index) => ({
        id: index,
        idConfiguracaoPropriedade: item.idConfiguracaoPropriedade,
        idLojaConfiguracao: item.idLojaConfiguracao,
        dsChave: item.configuracaoPropriedade?.dsChave,
        dsConfiguracao: item.configuracaoPropriedade?.dsConfiguracao,
        dsValor: item.dsValor,
        fgAtivo: getTypeChave(item.configuracaoPropriedade?.dsChave) !== 'fg' || item.dsValor === 'true',
        configuracaoPropriedade: {
            idConfiguracaoPropriedade: item.idConfiguracaoPropriedade,
            dsConfiguracao: item.configuracaoPropriedade?.dsConfiguracao,
            dsChave: item.configuracaoPropriedade?.dsChave,
        },
    }));

export const LojaConfiguracaoPropriedadeGrid = memo(function LojaConfiguracaoPropriedadeGrid({
    lojaConfiguracoes,
    setFieldValue,
    disabled = false,
}: {
    lojaConfiguracoes?: LojaConfiguracao[];
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    disabled?: boolean;
}) {
    const { isFetching } = useQueryConfiguracaoPropriedade();

    const rows = useMemo(() => getRows(lojaConfiguracoes), [lojaConfiguracoes]);

    /**
     * Retorna a cell de uma linha conforme seu tipo.
     */
    const getCell = useCallback(({ row }: GridRenderCellParams) => {
        const typeChave = getTypeChave(row.dsChave);
        const isPercentual = typeChave === 'pe';

        switch (typeChave) {
            case 'fg':
                return (
                    <Checkbox
                        checked={row.dsValor === 'true'}
                        onChange={(e) => setFieldValue(`lojaConfiguracoes[${row.id}]`, { ...row, dsValor: String(e.target.checked) })}
                        disabled={disabled}
                    />
                );
            case 'ds':
            case 'cd':
                return (
                    <CissTextField
                        size="small"
                        value={row.dsValor}
                        onChange={(e) => setFieldValue(`lojaConfiguracoes[${row.id}]`, { ...row, dsValor: e.target.value })}
                        disabled={disabled}
                    />
                );
            case 'vl':
            case 'pe':
                return (
                    <CissNumberField
                        size="small"
                        inputType="decimal"
                        value={row.dsValor}
                        maxValue={isPercentual ? 100 : undefined}
                        forceMaxValue={isPercentual}
                        onChange={(e) => setFieldValue(`lojaConfiguracoes[${row.id}]`, { ...row, dsValor: Number(e.target.value).toFixed(2) })}
                        disabled={disabled}
                    />
                );
        }
    }, []);

    const columns: GridColDef[] = [
        { field: 'dsConfiguracao', headerName: 'Configuração', minWidth: 120, flex: 1 },
        {
            field: 'Valor',
            align: 'center',
            headerName: 'Valor',
            headerAlign: 'center',
            sortable: false,
            hideable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 1,
            minWidth: 90,
            renderCell: getCell,
        },
    ];

    return <PanelGrid rows={rows} hideFooter={true} reload={false} loading={isFetching} columns={columns} remote={false} />;
});
